import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { encodeAdminPath } from "@/lib/adminRoutes";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LiveToggle } from "@/components/LiveToggle";
import {
  CheckCircle2, XCircle, Plug, RefreshCw, Save, Eye, EyeOff, KeyRound, Database, Loader2, AlertCircle,
} from "lucide-react";

// Per-section key definitions. `secret: true` hides the value by default.
const SECTIONS = [
  {
    id: "database",
    title: "Database — PostgreSQL connection",
    help: "Rotate the PostgreSQL connection string at runtime (e.g., flip from local dev DB to AWS RDS). The engine is recreated immediately after Save — no restart needed. Test connection runs SELECT 1 against the URL you've entered. WARNING: A bad URL will start failing requests on the next call.",
    flags: [
      { key: "DB_BACKEND", label: "DB backend", options: ["postgresql", "mysql"] },
    ],
    fields: [
      { key: "MYSQL_URL", label: "Database URL", secret: true, hint: "postgresql+asyncpg://USER:PASS@HOST:5432/DBNAME" },
    ],
  },
  {
    id: "manarecharge",
    title: "ManaRecharge (BBPS + Payouts + Payin Gateway)",
    help: "BBPS, Payouts, AND the Payin (hosted-checkout) gateway all share these credentials. Switch each provider flag to live independently. Test connection probes BBPS — use the separate ManaRecharge PG test below for the Payin gateway.",
    flags: [
      { key: "BBPS_PROVIDER", label: "BBPS provider", options: ["manarecharge", "mock"] },
      { key: "PAYOUT_PROVIDER", label: "Payout provider", options: ["manarecharge", "mock"] },
    ],
    fields: [
      { key: "MANA_BASE_URL", label: "Base URL", hint: "https://manarecharge.in/api/api/ (note the double /api/api/)" },
      { key: "MANA_AUTHTOKEN", label: "Authtoken", secret: true },
      { key: "MANA_XAPIKEY", label: "x-api-key", secret: true },
      { key: "MANA_TOKEN", label: "token", hint: "Body token for API requests" },
    ],
  },
  {
    id: "manarecharge_pg",
    title: "ManaRecharge · Payin Gateway (hosted checkout)",
    help: "Reuses the same credentials as the BBPS / Payouts section above. Test connection here probes the PG availability endpoint (pgstatus) — it surfaces auth, IP whitelist, and active-channel issues separately from BBPS.",
    flags: [],
    fields: [],
  },
  {
    id: "msg91",
    title: "MSG91 (SMS · OTP + notifications)",
    help: "Flip to live once DLT template + sender ID are approved. Credentials read per-call — no restart needed.",
    flags: [
      { key: "SMS_PROVIDER", label: "SMS provider", options: ["msg91", "mock"] },
    ],
    fields: [
      { key: "MSG91_AUTHKEY", label: "Auth Key", secret: true },
      { key: "MSG91_TEMPLATE_ID", label: "Template ID", hint: "DLT-approved OTP template" },
      { key: "MSG91_SENDER_ID", label: "Sender ID", hint: "6-letter header e.g. PAYFLX" },
      { key: "MSG91_DLT_ENTITY_ID", label: "DLT Entity ID" },
      { key: "MSG91_TXN_FLOW_ID", label: "Txn Flow ID", hint: "Optional — falls back to Template ID" },
    ],
  },
  {
    id: "razorpay",
    title: "Razorpay (Wallet top-up gateway)",
    help: "Get keys at dashboard.razorpay.com → Account & Settings → API Keys.",
    flags: [],
    fields: [
      { key: "RAZORPAY_KEY_ID", label: "Key ID", hint: "rzp_test_... or rzp_live_..." },
      { key: "RAZORPAY_KEY_SECRET", label: "Key Secret", secret: true },
      { key: "RAZORPAY_WEBHOOK_SECRET", label: "Webhook Secret", secret: true },
    ],
  },
  {
    id: "cashfree",
    title: "Cashfree (Wallet top-up gateway)",
    help: "Get keys at merchant.cashfree.com → Developers → API Keys.",
    flags: [
      { key: "GATEWAY_ENV", label: "Gateway environment", options: ["sandbox", "production"] },
    ],
    fields: [
      { key: "CASHFREE_APP_ID", label: "App ID" },
      { key: "CASHFREE_SECRET_KEY", label: "Secret Key", secret: true },
      { key: "CASHFREE_WEBHOOK_SECRET", label: "Webhook Secret", secret: true },
    ],
  },
  {
    id: "runpaisa",
    title: "Runpaisa (Wallet top-up gateway)",
    help: "Hosted-checkout flow per Runpaisa PG API v1.0.0. Three creds — Client ID + Username + Password — issued by Runpaisa. Switch between UAT (dev/test endpoints) and Production via the env flag. IP whitelisting on the Runpaisa dashboard is mandatory before any call works.",
    flags: [
      { key: "RUNPAISA_ENV", label: "Runpaisa environment", options: ["uat", "production"] },
    ],
    fields: [
      { key: "RUNPAISA_CLIENT_ID", label: "Client ID", hint: "Issued by Runpaisa" },
      { key: "RUNPAISA_USERNAME", label: "Username" },
      { key: "RUNPAISA_PASSWORD", label: "Password", secret: true },
    ],
  },
  {
    id: "hitch",
    title: "Hitch.zone (Parent system · OTP + KYC + Bank verify)",
    help: "PayFlex sits underneath the hitch.zone parent system. These credentials gate every customer OTP, KYC fetch (verification %, PAN, Aadhaar), bank-account verification (IFSC + listed accounts) and the DigiLocker Aadhaar flow. Both X-Channel-Signature and X-App-Signature are required by every v4 endpoint — rotate them in lockstep when hitch.zone reissues. Test connection probes /internal/user-mobile-kyc with a dummy mobile to verify the signatures + API key are accepted.",
    flags: [],
    fields: [
      { key: "HITCH_BASE_URL", label: "Base URL", hint: "e.g. https://www.hitch.zone (omit trailing slash)" },
      { key: "HITCH_PARTNER_ID", label: "Partner ID", hint: "Numeric ID — used in /v4/beneficiary/<id>/accounts" },
      { key: "HITCH_TOKEN_TTL_MINUTES", label: "Bearer token TTL (mins)", hint: "How long to cache hitch.zone customer login tokens (default 180 = 3h)" },
      { key: "HITCH_KYC_MIN_PERCENTAGE", label: "Auto-approve KYC %", hint: "Customers above this verification % skip PayFlex's manual KYC queue (default 50)" },
      { key: "HITCH_INTERNAL_API_KEY", label: "Internal API key", secret: true, hint: "Bearer token for /internal/* endpoints" },
      { key: "HITCH_CHANNEL_SIGNATURE", label: "X-Channel-Signature", secret: true, hint: "UUID issued by hitch.zone — required on every v4 call" },
      { key: "HITCH_APP_SIGNATURE", label: "X-App-Signature", secret: true, hint: "Base64 JWT-ish blob — required on every v4 call" },
    ],
  },
  {
    id: "live_relay",
    title: "Live Relay Proxy (Preview ↔ Production)",
    help: "Connects this preview/staging environment to the live production system. Preview cannot call gateways directly (IP blocked), so all API calls and DB reads are proxied through the live system. Set the live system URL and a shared secret. Both preview and live must have the same RELAY_SECRET.",
    flags: [],
    fields: [
      { key: "LIVE_RELAY_URL", label: "Live System URL", hint: "e.g. https://flexpay.today — the production URL" },
      { key: "RELAY_SECRET", label: "Relay Secret", secret: true, hint: "Shared secret between preview and live. Must match on both systems." },
    ],
  },
  {
    id: "workflow",
    title: "Approval Workflow",
    help: "Tune how L3-approved transactions get processed. When 'Hold after final approval' is ON, any txn that completes L3 is parked in a `processing_held` state instead of immediately calling the provider. An admin must click 'Process now' on the Approvals page to fire the actual BBPS/Payout call. Useful when provider credentials are flaky and you want a manual gate before money moves.",
    flags: [],
    fields: [
      { key: "HOLD_AFTER_FINAL_APPROVAL", label: "Hold after final approval (ON / OFF)", hint: "Set to 'true' or '1' to enable. Leave blank or 'false' for auto-process (default)." },
    ],
  },
];

// Flatten into a lookup — used to decide if a key's value is currently set (backend never returns the secret value, just presence)
const allKeys = SECTIONS.flatMap((s) => [...s.flags.map((f) => f.key), ...s.fields.map((f) => f.key)]);

export default function AdminIntegrations() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // Local form state for unsaved edits keyed by env name
  const [form, setForm] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  const [testingSection, setTestingSection] = useState(null);
  const [testResult, setTestResult] = useState({}); // { sectionId: {ok, message} }

  // 2FA for credential updates
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyStep, setVerifyStep] = useState("password"); // "password" | "otp"
  const [verifyPassword, setVerifyPassword] = useState("");
  const [verifyOtp, setVerifyOtp] = useState("");
  const [verifySessionId, setVerifySessionId] = useState(null);
  const [verifyMobile, setVerifyMobile] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [pendingSave, setPendingSave] = useState(null); // { section, patch }
  // Phase 7.22c — Refresh catalog
  const [refreshing, setRefreshing] = useState(false);
  const [catalogSnapshot, setCatalogSnapshot] = useState(null); // { items: [...] }
  const [refreshResult, setRefreshResult] = useState(null);

  const loadSnapshot = async () => {
    try {
      const { data } = await api.get("/admin/integrations/catalog-snapshot");
      setCatalogSnapshot(data);
    } catch { /* admin only — silent on non-admin pages */ }
  };
  useEffect(() => { loadSnapshot(); }, []);

  const refreshCatalog = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const { data } = await api.post("/admin/integrations/refresh-catalog");
      setRefreshResult(data);
      if (data.ok) {
        toast.success(`Refreshed · ${data.total_billers} billers across ${Object.keys(data.per_category || {}).length} categories in ${data.elapsed_ms}ms`);
      } else {
        toast.error(data.provider_message || data.error || "Refresh failed");
      }
      await loadSnapshot();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const testSection = async (section) => {
    setTestingSection(section.id);
    try {
      const { data } = await api.post(`/admin/integrations/test/${section.id}`);
      setTestResult((r) => ({ ...r, [section.id]: { ok: data.ok, message: data.ok ? data.message : `Failed: ${data.message || `HTTP ${data.code}`}` } }));
      if (data.ok) toast.success(`${section.id} · ${data.message}`);
      else toast.error(`${section.id} · ${data.message || `HTTP ${data.code}`}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Probe failed";
      setTestResult((r) => ({ ...r, [section.id]: { ok: false, message: msg } }));
      toast.error(msg);
    } finally { setTestingSection(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/status");
      setStatus(data);
      // Prefill form with current flag values from backend (always override)
      const flagKeys = ["BBPS_PROVIDER", "PAYOUT_PROVIDER", "SMS_PROVIDER", "GATEWAY_ENV", "RUNPAISA_ENV", "DB_BACKEND"];
      const init = {};
      init["BBPS_PROVIDER"] = data.bbps?.env_flag || "manarecharge";
      init["PAYOUT_PROVIDER"] = data.payout?.env_flag || "manarecharge";
      init["SMS_PROVIDER"] = data.sms?.env_flag || "mock";
      init["GATEWAY_ENV"] = data.wallet_gateways?.env || "sandbox";
      init["RUNPAISA_ENV"] = data.wallet_gateways?.runpaisa?.RUNPAISA_ENV || "uat";
      init["DB_BACKEND"] = data.database?.backend || "postgresql";
      // Flag keys always reflect backend truth; text field edits are preserved
      setForm((prev) => {
        const merged = { ...prev };
        for (const k of flagKeys) merged[k] = init[k];
        return merged;
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isKeyPresent = (key) => {
    if (!status) return false;
    if (key === "BBPS_PROVIDER") return bool(status.bbps?.configured);
    if (key === "PAYOUT_PROVIDER") return bool(status.payout?.configured);
    if (key === "SMS_PROVIDER") return bool(status.sms?.configured);
    if (key === "GATEWAY_ENV") return true;
    if (key === "DB_BACKEND") return true;
    if (key === "MYSQL_URL") return Boolean(status.database?.url_redacted);
    if (key.startsWith("MANA_")) return Boolean(status.manarecharge_credentials?.[key]);
    if (key.startsWith("MSG91_")) return Boolean(status.sms?.msg91_credentials?.[key]);
    if (key.startsWith("RAZORPAY_")) return Boolean(status.wallet_gateways?.razorpay?.[key]);
    if (key.startsWith("CASHFREE_")) return Boolean(status.wallet_gateways?.cashfree?.[key]);
    if (key === "RUNPAISA_ENV") return true;
    if (key.startsWith("RUNPAISA_")) return Boolean(status.wallet_gateways?.runpaisa?.[key]);
    if (key === "LIVE_RELAY_URL" || key === "RELAY_SECRET") return Boolean(status.live_relay?.[key]);
    if (key.startsWith("HITCH_")) return Boolean(status.hitch?.[key]);
    if (key === "HOLD_AFTER_FINAL_APPROVAL") return Boolean(status.workflow?.[key]);
    return false;
  };

  const saveSection = async (section) => {
    const keys = [...section.flags.map((f) => f.key), ...section.fields.map((f) => f.key)];
    const patch = {};
    const secretKeys = new Set(section.fields.filter((f) => f.secret).map((f) => f.key));
    keys.forEach((k) => {
      const v = form[k];
      // Skip secret fields that are empty (don't overwrite saved values with blank)
      if (secretKeys.has(k) && (!v || v.trim() === "")) return;
      if (v !== undefined && v !== null) patch[k] = v;
    });
    if (Object.keys(patch).length === 0) {
      toast.info("Nothing to save in this section");
      return;
    }
    // Live Relay & Workflow toggles are server-to-server / infra configuration
    // (not payment credentials) — skip 2FA OTP.
    if (section.id === "live_relay" || section.id === "workflow") {
      setSavingSection(section.id);
      try {
        const { data } = await api.patch("/admin/env", patch);
        toast.success(`Saved ${data.updated.length} key(s) · ${section.title.split(" ")[0]}`);
        const cleared = { ...form };
        section.fields.forEach((f) => { if (f.secret) delete cleared[f.key]; });
        setForm(cleared);
        await load();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
      } finally {
        setSavingSection(null);
      }
      return;
    }
    // Open 2FA dialog instead of saving directly
    setPendingSave({ section, patch });
    setVerifyStep("password");
    setVerifyPassword("");
    setVerifyOtp("");
    setVerifySessionId(null);
    setVerifyMobile("");
    setVerifyOpen(true);
  };

  const handlePasswordVerify = async () => {
    if (!verifyPassword.trim()) { toast.error("Enter your password"); return; }
    setVerifyBusy(true);
    try {
      const { data } = await api.post("/admin/env/request-otp", { password: verifyPassword });
      setVerifySessionId(data.otp_session_id);
      setVerifyMobile(data.mobile_masked);
      setVerifyStep("otp");
      toast.success(`OTP sent to ${data.mobile_masked}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Password verification failed");
    } finally { setVerifyBusy(false); }
  };

  const handleOtpVerify = async () => {
    if (!verifyOtp.trim() || verifyOtp.length < 4) { toast.error("Enter the OTP"); return; }
    if (!pendingSave || !verifySessionId) return;
    setVerifyBusy(true);
    const { section, patch } = pendingSave;
    setSavingSection(section.id);
    try {
      const finalPayload = { ...patch, _otp_session_id: verifySessionId, _otp_code: verifyOtp };
      const { data } = await api.patch("/admin/env", finalPayload);
      toast.success(`Saved ${data.updated.length} key(s) · ${section.title.split(" ")[0]}`);
      if (data.db_rotated) {
        toast.success("Database connection rotated — new engine is live", { duration: 5000 });
      }
      const cleared = { ...form };
      section.fields.forEach((f) => { if (f.secret) delete cleared[f.key]; });
      setForm(cleared);
      setVerifyOpen(false);
      setPendingSave(null);
      // Always reload the full status — the PATCH response only returns a
      // subset (provider_status + sms_status), so per-section blocks like
      // `hitch` / `live_relay` / `database` would otherwise show stale until
      // the user hits "Reload" manually.
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setVerifyBusy(false);
      setSavingSection(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Integrations
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Edit provider credentials live. Values are applied to the running backend and persisted
            to <code className="font-mono text-xs bg-accent px-1 py-0.5 rounded">/app/backend/.env</code> — no restart required.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-status">
          <RefreshCw className="h-4 w-4 mr-1" strokeWidth={1.5} /> Reload
        </Button>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 xl:grid-cols-4 gap-6">
        <ProviderCard title="BBPS"         provider={status?.bbps?.provider}                 flag={status?.bbps?.env_flag}    flagName="BBPS_PROVIDER" />
        <ProviderCard title="Payouts"      provider={status?.payout?.provider}               flag={status?.payout?.env_flag}  flagName="PAYOUT_PROVIDER" />
        <ProviderCard title="SMS · OTP"    provider={status?.sms?.provider}                  flag={status?.sms?.env_flag}     flagName="SMS_PROVIDER" />
        <ProviderCard title="Wallet Gateway" provider={
          status?.wallet_gateways?.razorpay?.enabled ? "razorpay" :
          status?.wallet_gateways?.cashfree?.enabled ? "cashfree" :
          status?.wallet_gateways?.manarecharge?.enabled ? "manarecharge" :
          status?.wallet_gateways?.runpaisa?.enabled ? "runpaisa" : "not configured"
        } flag={status?.wallet_gateways?.env} flagName="GATEWAY_ENV" />
      </div>

      {/* Database status card — visible health + redacted URL */}
      {status?.database && (
        <div className="mt-4 surface-card p-4 border-l-4 border-l-success" data-testid="database-status-card">
          <div className="flex items-start gap-3 flex-wrap">
            <Database className="h-5 w-5 text-success mt-0.5" strokeWidth={1.5} />
            <div className="flex-1 min-w-[200px]">
              <div className="font-display font-semibold text-sm flex items-center gap-2">
                Database — {status.database.backend?.toUpperCase() || "MYSQL"}
                {status.database.ok ? (
                  <Badge variant="outline" className="border-success/40 text-success text-[10px]">connected</Badge>
                ) : (
                  <Badge variant="outline" className="border-destructive/40 text-destructive text-[10px]">unreachable</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {status.database.url_redacted ? (
                  <>Connected to <code className="font-mono bg-accent px-1.5 py-0.5 rounded">{status.database.url_redacted}</code></>
                ) : (
                  "No DB URL configured"
                )}
                {status.database.ok === false && status.database.error && (
                  <span className="block text-destructive mt-1">{status.database.error}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Outbound IP banner — needed for ManaRecharge / Paysharp / live PG IP whitelisting */}
      {status?.outbound_ip && (
        <div className="mt-6 surface-card p-4 flex items-start gap-3 border-l-4 border-l-secondary" data-testid="outbound-ip-banner">
          <KeyRound className="h-5 w-5 text-secondary mt-0.5" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="font-display font-semibold text-sm">Outbound IP — whitelist this on every live provider's dashboard</div>
            <p className="text-xs text-muted-foreground mt-1">
              Some providers (ManaRecharge, Paysharp, certain PGs) require IP whitelisting before any API call works. Add this IP to the provider's allowed list:
            </p>
          </div>
          <div className="font-mono text-sm font-bold bg-accent px-3 py-1.5 rounded select-all" data-testid="outbound-ip-value">{status.outbound_ip}</div>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(status.outbound_ip); toast.success("IP copied"); }} data-testid="copy-outbound-ip">
            Copy
          </Button>
        </div>
      )}

      {/* Live / Preview data-source toggle — relocated from the global admin
          header into this Integrations page so it sits next to the DB + provider
          config it actually affects. */}
      <div className="mt-4 surface-card p-4 border-l-4 border-l-secondary" data-testid="live-source-card">
        <div className="flex items-start gap-3 flex-wrap">
          <Plug className="h-5 w-5 text-secondary mt-0.5" strokeWidth={1.5} />
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-semibold text-sm">Live / Preview data source</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Controls which dataset the admin panel reads — your local DB
              (<b>Preview</b>) or the production system via the relay (<b>Live</b>).
              This setting also routes provider API calls (e.g. ManaRecharge
              <code className="font-mono"> pgstatus</code>) through the relay when
              the live system's IP is whitelisted but the preview's isn't.
            </p>
          </div>
          <div className="shrink-0">
            <LiveToggle />
          </div>
        </div>
      </div>

      {/* Webhook URLs — admin copies these into the provider dashboard */}
      {status?.webhook_urls && (
        <div className="mt-4 surface-card p-4 border-l-4 border-l-primary" data-testid="webhook-urls-card">
          <div className="flex items-start gap-3">
            <Plug className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-display font-semibold text-sm">Webhook URLs — paste into each provider's dashboard</div>
              <p className="text-xs text-muted-foreground mt-1">
                We re-verify every webhook by calling the provider's status API server-to-server, so spoofed payloads cannot credit a wallet. ManaRecharge has no signature header — re-verification is the security boundary.
              </p>
            </div>
            <Button variant="outline" size="sm" asChild data-testid="open-webhook-events">
              <a href={encodeAdminPath("/admin/webhook-events")}>View events log</a>
            </Button>
          </div>
          <div className="mt-4 space-y-2">
            {Object.entries(status.webhook_urls).map(([prov, url]) => (
              <div key={prov} className="flex items-center gap-3 flex-wrap" data-testid={`webhook-row-${prov}`}>
                <span className="text-xs uppercase tracking-widest font-mono w-28 text-muted-foreground">{prov}</span>
                <code className="flex-1 min-w-[260px] font-mono text-xs bg-accent px-2.5 py-1.5 rounded select-all break-all" data-testid={`webhook-url-${prov}`}>{url}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(url); toast.success(`${prov} webhook URL copied`); }} data-testid={`copy-webhook-${prov}`}>
                  Copy
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 7.22c — Refresh BBPS catalog (categories + billers) */}
      <div className="mt-4 surface-card p-4 border-l-4 border-l-success" data-testid="refresh-catalog-card">
        <div className="flex items-start gap-3 flex-wrap">
          <Database className="h-5 w-5 text-success mt-0.5" strokeWidth={1.5} />
          <div className="flex-1 min-w-[260px]">
            <div className="font-display font-semibold text-sm">BBPS catalog · ManaRecharge</div>
            <p className="text-xs text-muted-foreground mt-1">
              Re-fetch the live category list, all 11 BBPS billers and their `ad_fields` metadata.
              Run this when partners report stale or missing operators in the BBPS wizard.
              Snapshots are persisted in <code className="font-mono text-[10px] bg-accent px-1 rounded">db.provider_catalog_cache</code>.
            </p>
          </div>
          <Button onClick={refreshCatalog} disabled={refreshing} data-testid="refresh-catalog-btn">
            {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh catalog now
          </Button>
        </div>

        {refreshResult && (
          <div className={`mt-4 rounded-md border p-3 text-sm ${refreshResult.ok ? "border-success/40 bg-success/5" : "border-warning/40 bg-warning/5"}`} data-testid="refresh-result">
            {refreshResult.ok ? (
              <>
                <div className="text-xs font-medium text-success flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Refreshed in {refreshResult.elapsed_ms}ms · {refreshResult.total_billers} billers across {refreshResult.categories_count} categories
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                  {Object.entries(refreshResult.per_category || {}).map(([cat, info]) => (
                    <div key={cat} className="flex items-center justify-between text-[11px] font-mono bg-background/50 px-2 py-1 rounded">
                      <span className="text-muted-foreground">{cat}</span>
                      <span className={`font-bold ${info.count > 0 ? "text-success" : "text-warning"}`}>{info.count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="text-xs font-medium text-warning flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" /> {refreshResult.error || "Refresh failed"}
                </div>
                {refreshResult.provider_message && (
                  <div className="mt-1.5 text-xs text-muted-foreground italic">
                    Provider says: "{refreshResult.provider_message}"
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!refreshResult && catalogSnapshot?.items?.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5" data-testid="snapshot-grid">
            {catalogSnapshot.items
              .filter((i) => i.category)
              .map((i) => (
                <div key={i.category} className="flex items-center justify-between text-[11px] font-mono bg-background/50 px-2 py-1 rounded border">
                  <span className="text-muted-foreground">{i.category}</span>
                  <span className={`font-bold ${i.count > 0 ? "text-success" : "text-warning"}`}>{i.count}</span>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="mt-10 space-y-8">
        {SECTIONS.map((section) => (
          <section key={section.id} className="surface-card p-6" data-testid={`section-${section.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-display text-xl font-bold tracking-tight">{section.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{section.help}</p>
                {testResult[section.id] && (
                  <div
                    className={`mt-3 inline-flex items-center gap-2 text-xs rounded-md border px-2.5 py-1.5 font-medium ${testResult[section.id].ok ? "border-success/40 bg-success/10 text-success" : "border-destructive/40 bg-destructive/10 text-destructive"}`}
                    data-testid={`test-result-${section.id}`}
                  >
                    {testResult[section.id].ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    {testResult[section.id].message}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => testSection(section)}
                  disabled={testingSection === section.id || savingSection === section.id || loading}
                  data-testid={`test-${section.id}`}
                >
                  {testingSection === section.id
                    ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" strokeWidth={1.5} />
                    : <Plug className="h-4 w-4 mr-1" strokeWidth={1.5} />}
                  Test connection
                </Button>
                <Button
                  onClick={() => saveSection(section)}
                  disabled={savingSection === section.id || loading}
                  data-testid={`save-${section.id}`}
                >
                  {savingSection === section.id ? (
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <Save className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  )}
                  Save {section.id}
                </Button>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
              {section.flags.map((f) => (
                <div key={f.key} data-testid={`field-${f.key}`}>
                  <Label className="text-xs font-mono">{f.key}</Label>
                  <Select
                    value={form[f.key] || f.options[0]}
                    onValueChange={(v) => setForm({ ...form, [f.key]: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid={`input-${f.key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">{f.label}</p>
                </div>
              ))}

              {section.fields.map((f) => {
                const present = isKeyPresent(f.key);
                const shown = showSecret[f.key];
                return (
                  <div key={f.key} data-testid={`field-${f.key}`}>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-mono flex items-center gap-1.5">
                        <KeyRound className="h-3 w-3 text-muted-foreground" />{f.key}
                      </Label>
                      {present ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-success">
                          <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} /> set
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                          <XCircle className="h-3 w-3" strokeWidth={1.5} /> empty
                        </span>
                      )}
                    </div>
                    <div className="relative mt-1">
                      <Input
                        type={f.secret && !shown ? "password" : "text"}
                        value={form[f.key] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        placeholder={f.secret && present ? "•••••••••••• (hidden — type new value to rotate)" : (f.hint || f.label)}
                        className="font-mono text-sm pr-10"
                        data-testid={`input-${f.key}`}
                      />
                      {f.secret && (
                        <button
                          type="button"
                          onClick={() => setShowSecret({ ...showSecret, [f.key]: !shown })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                          data-testid={`toggle-${f.key}`}
                        >
                          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                    {f.hint && <p className="text-[11px] text-muted-foreground mt-1">{f.hint}</p>}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* 2FA Verification Dialog for credential updates */}
      <Dialog open={verifyOpen} onOpenChange={(open) => { if (!open) { setVerifyOpen(false); setPendingSave(null); } }}>
        <DialogContent className="max-w-sm" data-testid="env-2fa-dialog">
          <DialogHeader>
            <DialogTitle>{verifyStep === "password" ? "Verify your identity" : "Enter OTP"}</DialogTitle>
            <DialogDescription>
              {verifyStep === "password"
                ? "Enter your admin password to proceed with credential update."
                : `OTP sent to ${verifyMobile}. Enter it below to confirm the update.`}
            </DialogDescription>
          </DialogHeader>
          {verifyStep === "password" ? (
            <div className="space-y-3">
              <div>
                <Label>Admin password</Label>
                <Input
                  type="password" autoFocus
                  value={verifyPassword}
                  onChange={(e) => setVerifyPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handlePasswordVerify()}
                  placeholder="Enter your password"
                  className="mt-1"
                  data-testid="env-2fa-password"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setVerifyOpen(false)}>Cancel</Button>
                <Button onClick={handlePasswordVerify} disabled={verifyBusy} data-testid="env-2fa-submit-password">
                  {verifyBusy ? "Verifying..." : "Send OTP"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>OTP code</Label>
                <Input
                  type="text" autoFocus maxLength={6}
                  value={verifyOtp}
                  onChange={(e) => setVerifyOtp(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handleOtpVerify()}
                  placeholder="6-digit OTP"
                  className="mt-1 font-mono text-center text-lg tracking-widest"
                  data-testid="env-2fa-otp"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setVerifyStep("password")}>Back</Button>
                <Button onClick={handleOtpVerify} disabled={verifyBusy} data-testid="env-2fa-submit-otp">
                  {verifyBusy ? "Saving..." : "Verify & Save"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function ProviderCard({ title, provider, flag, flagName }) {
  const isLive = provider && !provider.startsWith("mock");
  return (
    <div className="surface-card p-6 interactive-card relative overflow-hidden">
      <div className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-primary/5" />
      <div className="relative flex items-center justify-between">
        <div className="h-10 w-10 rounded-md bg-accent text-primary grid place-items-center">
          <Plug className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <Badge
          className={isLive ? "bg-success text-success-foreground" : ""}
          variant={isLive ? "default" : "outline"}
        >
          {isLive ? "Live" : "Mock"}
        </Badge>
      </div>
      <div className="font-display text-xl font-bold mt-4">{title}</div>
      <div className="text-sm mt-1 font-mono">{provider || "—"}</div>
      <div className="mt-3 text-xs text-muted-foreground">
        <code className="bg-accent px-1 py-0.5 rounded font-mono">{flagName}={flag || "manarecharge"}</code>
      </div>
    </div>
  );
}
